import React from 'react';
import { FixMeLater } from '../../../utils';
import { ImageParams, ImageCropParamKeys, ImageTemplate, ImageGalleryRawImage } from '../ImageGallery.types';
import { STATIC_STYLES } from './ImageGalleryGrid.styles';
import { Styler } from '../../Styler';
import { Picture, PictureProps } from '../../Picture';
import { generateImageLayout } from './utils/generateImageLayout';
import { buildResponsiveImageSet } from '../utils/buildResponsiveImageSet';
import { IMAGE_GRID_DEFAULT_TEMPLATES } from './ImageGalleryGrid.constants';
import { enrichRawImage } from './utils/enrichRawImage';

export interface ImageGalleryGridProps {
    /**
     * An array of image information.
     */
    images: Array<ImageGalleryRawImage>;

    /**
     * The width and height parameters used to request image crops.
     * @default Object: { widthKey: "w", heightKey: "h" }
     */
    cropParamKeys?: ImageCropParamKeys;

    /**
     * Any additonal parameters which should be appended when requesting image crops.
     */
    additionalCropParams?: ImageParams;

    /**
     * An array of image templates which will be used to compose the grid layout.
     */
    templates?: ImageTemplate[];

    /**
     * Callback fired when and image has been clicked.
     */
    onImageClick?: (event: React.SyntheticEvent, index: number) => void;

    /**
     * Additonal properties applied to each Picture component.
     */
    PictureProps?: Partial<PictureProps>;
}

export const ImageGalleryGrid = React.forwardRef(function ImageGalleryGrid(
    props: ImageGalleryGridProps,
    ref: React.ForwardedRef<FixMeLater>
): React.JSX.Element {
    const {
        images,
        cropParamKeys = { widthKey: 'w', heightKey: 'h' },
        additionalCropParams = {},
        templates = IMAGE_GRID_DEFAULT_TEMPLATES,
        onImageClick,
        PictureProps: PicturePropsProp,
        ...other
    } = props;

    const enrichedImages = React.useMemo(() => {
        return images.map((img, index) => enrichRawImage(img, index));
    }, [images]);

    const layout = React.useMemo(() => {
        return generateImageLayout(enrichedImages, templates, []);
    }, [enrichedImages, templates]);

    return (
        <div ref={ref} css={STATIC_STYLES} data-image-gallery-grid={true} {...other}>
            {layout.map((block) => {
                const { template, usedImagesBySlot } = block;

                // Construct a stable key from template id plus usedImages ids
                const blockKey = `${template.id}-${usedImagesBySlot.map((img) => img?.id ?? 'empty').join('-')}`;

                return (
                    <div
                        key={blockKey}
                        data-grid-template={true}
                        css={{
                            gridTemplateColumns: template.gridTemplateColumns,
                            gridTemplateAreas: template.gridTemplateAreas.map((area) => `"${area}"`).join('\n'),
                        }}
                    >
                        {template.slots.map((slot, slotIndex) => {
                            const image = usedImagesBySlot[slotIndex];
                            const { gridArea, shape, isSpanning } = slot;

                            const hasImage = image != null;

                            const slotKey = hasImage ? `img-${image.id}` : `empty-${template.id}-${gridArea}`;

                            // Return the image or an empty placeholder
                            return (
                                <Styler
                                    key={slotKey}
                                    data-image-slot={true}
                                    // If the slot spans other slots, the grid gap will distort it's aspect ratio.
                                    // Therefore, we just allow this slot to fill the remaining space instead of forcing an exact shape.
                                    data-shape={!isSpanning ? shape : undefined}
                                    style={{ gridArea }}
                                    {...(hasImage ? { button: true, tabIndex: 0, 'aria-label': image.label } : {})}
                                    onClick={
                                        hasImage
                                            ? (event: React.SyntheticEvent) =>
                                                  onImageClick?.(event, image.originalIndex)
                                            : undefined
                                    }
                                    {...other}
                                >
                                    {hasImage && (
                                        <Picture
                                            imageSet={buildResponsiveImageSet(
                                                image.url,
                                                slot.cropDimensions,
                                                cropParamKeys,
                                                additionalCropParams
                                            )}
                                            {...PicturePropsProp}
                                        />
                                    )}
                                </Styler>
                            );
                        })}
                    </div>
                );
            })}
        </div>
    );
});
